import React, { useState, useEffect, useMemo } from 'react';
import {
    Package,
    ChevronRight,
    Clock,
    CheckCircle2,
    Play,
    FileText,
    Loader2,
    Layers,
    RefreshCw,
    Search,
    ArrowRight,
    Store,
    ListTree,
    Wrench,
    Boxes,
    Plus
} from 'lucide-react';

// Import fetchProductionOrders and fetchStoresWhereStaffAssigned
import { fetchProductionOrders, fetchStoresWhereStaffAssigned } from '../apiHelper';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useNavigate } from 'react-router-dom';

// Ordered Status Workflow Configuration
const STATUS_STEPS = [
    { id: 'draft', label: 'Draft', icon: FileText, color: 'text-zinc-500', bg: 'bg-zinc-100', border: 'border-zinc-300' },
    { id: 'confirmed', label: 'Confirmed', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    { id: 'in progress', label: 'In Progress', icon: Play, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    { id: 'completed', label: 'Completed', icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' }
];

export const ProductionOrderFlowManager = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeDetailTab, setActiveDetailTab] = useState('bom'); // 'bom' | 'routing'

    const [storeOptions, setStoreOptions] = useState([]);
    const [selectedStore, setSelectedStore] = useState('');

    // Fetch user assigned stores on mount
    useEffect(() => {
        const init = async () => {
            try {
                const stored = localStorage.getItem('user');
                const parsed = stored ? JSON.parse(stored) : null;
                setUser(parsed);

                const stores = await fetchStoresWhereStaffAssigned(parsed);
                if (stores && stores.length > 0) {
                    const filteredStores = stores.filter(store => store.storetype !== 'WHS');
                    const mapped = filteredStores.map(s => ({
                        label: s.identifier || `Branch ${s.store_id}`,
                        value: s.store_id.toString()
                    }));

                    setStoreOptions(mapped);
                    if (mapped.length > 0) {
                        setSelectedStore(mapped[0].value);
                    }
                } else {
                    console.error("No store locations assigned.");
                }
            } catch (error) {
                console.error("Failed to initialize stores:", error);
            }
        };
        init();
    }, []);

    // Fetch Production Orders using the user-selected store ID
    const loadOrders = async () => {
        if (!selectedStore) return;
        setLoading(true);
        try {
            const response = await fetchProductionOrders({
                store_id: parseInt(selectedStore, 10),
                offset: 0,
                limit: 1000
            });

            const fetchedList = Array.isArray(response) ? response : (response?.items || response?.data || []);
            setOrders(fetchedList);

            console.log(fetchedList)

            if (fetchedList.length > 0) {
                setSelectedOrderId(fetchedList[0].productionorder_id?.toString() || fetchedList[0].id?.toString());
            } else {
                setSelectedOrderId(null);
            }
        } catch (error) {
            console.error("Failed to load production orders:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedStore) {
            loadOrders();
        }
    }, [selectedStore]);

    // Active selected order details
    const selectedOrder = useMemo(() => {
        return orders.find(
            (o) => (o.productionorder_id?.toString() || o.id?.toString()) === selectedOrderId
        );
    }, [orders, selectedOrderId]);

    // Filtered orders list
    const filteredOrders = useMemo(() => {
        return orders.filter((order) => {
            const matchesStatus = statusFilter === 'all' || (order.status || '').toLowerCase() === statusFilter;
            const productName = order.product_name || order.bom?.description || order.routing?.description || 'Finished Product';
            const orderId = (order.productionorder_id || order.id || '').toString();
            const matchesSearch = productName.toLowerCase().includes(searchQuery.toLowerCase()) || orderId.includes(searchQuery);
            return matchesStatus && matchesSearch;
        });
    }, [orders, statusFilter, searchQuery]);

    // Utility to find step index in Status Flow
    const getStatusIndex = (status) => {
        const formatted = (status || 'draft').toLowerCase();
        return STATUS_STEPS.findIndex((s) => s.id === formatted);
    };

    return (
        <div className="space-y-6 max-w-7xl mx-auto px-2 sm:px-4 py-4">

            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-zinc-200 pb-4">
                <div>
                    <h2 className="text-xl font-bold text-zinc-900 tracking-tight flex items-center gap-2">
                        <Layers className="text-blue-600" size={22} /> Production Orders & Process Flow
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Monitor shop floor progression, component materials, and routing operations.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {storeOptions.length > 0 && (
                        <div className="relative flex items-center">
                            <Store size={14} className="absolute left-2.5 text-zinc-500 pointer-events-none" />
                            <select
                                value={selectedStore}
                                onChange={(e) => setSelectedStore(e.target.value)}
                                className="pl-8 pr-3 py-2 text-xs bg-white border border-zinc-300 rounded-lg font-medium text-zinc-800 shadow-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
                            >
                                {storeOptions.map((st) => (
                                    <option key={st.value} value={st.value}>
                                        {st.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <Button
                        onClick={loadOrders}
                        disabled={loading || !selectedStore}
                        className="bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs px-3 py-2 border border-zinc-300"
                    >
                        <RefreshCw size={14} className={`mr-1.5 inline ${loading ? 'animate-spin' : ''}`} /> Refresh Data
                    </Button>

                    <Button
                        onClick={() => navigate('/jobs/new-workflow')}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 shadow-sm flex items-center font-semibold"
                    >
                        <Plus size={14} className="mr-1 inline" /> Start New Job
                    </Button>
                </div>
            </div>

            {/* Main Interactive Flow Visualizer for Selected Production Order */}
            <Card className="p-5 border-zinc-200 shadow-sm bg-white overflow-hidden">
                <div className="border-b border-zinc-100 pb-3 mb-5 flex flex-wrap justify-between items-center gap-2">
                    <div>
                        <span className="text-[10px] font-bold tracking-wider text-blue-600 uppercase bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                            Process Flow Diagram
                        </span>
                        <h3 className="text-base font-bold text-zinc-900 mt-1">
                            {selectedOrder
                                ? `Order #${selectedOrder.productionorder_id || selectedOrder.id} - ${selectedOrder.product_name || selectedOrder.bom?.description || 'Finished Product'}`
                                : 'Select an Order to View Lifecycle'}
                        </h3>
                    </div>
                    {selectedOrder && (
                        <div className="text-right text-xs text-zinc-500">
                            <span>Target Quantity: <strong className="text-zinc-800">{selectedOrder.quantity_planned || 0} units</strong></span>
                        </div>
                    )}
                </div>

                {selectedOrder ? (
                    <div className="py-2 space-y-6">
                        {/* Horizontal Dynamic Process Flow Pipeline */}
                        <div className="relative flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4">
                            {STATUS_STEPS.map((step, index) => {
                                const currentStatusIdx = getStatusIndex(selectedOrder.status);
                                const isCompleted = index < currentStatusIdx;
                                const isCurrent = index === currentStatusIdx;
                                const StepIcon = step.icon;

                                return (
                                    <React.Fragment key={step.id}>
                                        <div
                                            className={`flex-1 p-4 rounded-xl border transition-all duration-200 relative ${isCurrent
                                                ? 'border-blue-600 bg-blue-50/40 shadow-sm ring-1 ring-blue-600'
                                                : isCompleted
                                                    ? 'border-emerald-200 bg-emerald-50/30'
                                                    : 'border-zinc-200 bg-zinc-50 opacity-60'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className={`p-2 rounded-lg ${isCurrent ? 'bg-blue-600 text-white' : isCompleted ? 'bg-emerald-600 text-white' : 'bg-zinc-200 text-zinc-500'
                                                    }`}>
                                                    <StepIcon size={18} />
                                                </span>
                                                <span className="text-[10px] font-mono font-bold text-zinc-400 uppercase">
                                                    Step 0{index + 1}
                                                </span>
                                            </div>

                                            <h4 className="text-sm font-bold text-zinc-900 mb-0.5">{step.label}</h4>
                                            <p className="text-[11px] text-zinc-500">
                                                {isCompleted && "Completed"}
                                                {isCurrent && "Currently Active"}
                                                {index > currentStatusIdx && "Pending"}
                                            </p>

                                            {isCurrent && (
                                                <div className="mt-2 text-[10px] inline-block font-semibold bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                                    Live Stage
                                                </div>
                                            )}
                                        </div>

                                        {index < STATUS_STEPS.length - 1 && (
                                            <div className="hidden md:flex items-center justify-center text-zinc-300 px-1">
                                                <ArrowRight size={20} className={index < currentStatusIdx ? 'text-emerald-500' : 'text-zinc-300'} />
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Selected Order Metadata Bar */}
                        <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                            <div>
                                <span className="text-zinc-400 block">Planned Start:</span>
                                <span className="font-semibold text-zinc-700">
                                    {selectedOrder.start_date_planned ? new Date(selectedOrder.start_date_planned).toLocaleDateString() : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-zinc-400 block">Planned End:</span>
                                <span className="font-semibold text-zinc-700">
                                    {selectedOrder.end_date_planned ? new Date(selectedOrder.end_date_planned).toLocaleDateString() : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-zinc-400 block">BOM Reference:</span>
                                <span className="font-semibold text-zinc-700">
                                    {selectedOrder.bom?.description || `Rev #${selectedOrder.bom?.version || '1'}`}
                                </span>
                            </div>
                            <div>
                                <span className="text-zinc-400 block">Routing Reference:</span>
                                <span className="font-semibold text-zinc-700">
                                    {selectedOrder.routing?.description || `Rev #${selectedOrder.routing?.version || '1'}`}
                                </span>
                            </div>
                        </div>

                        {/* DETAILED DATA TABS (BOM LINES & ROUTING OPERATIONS) */}
                        <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                            <div className="flex border-b border-zinc-200 bg-zinc-50">
                                <button
                                    onClick={() => setActiveDetailTab('bom')}
                                    className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeDetailTab === 'bom'
                                            ? 'border-blue-600 text-blue-600 bg-white'
                                            : 'border-transparent text-zinc-600 hover:text-zinc-900'
                                        }`}
                                >
                                    <Boxes size={15} /> Bill of Materials (BOM Lines)
                                </button>
                                <button
                                    onClick={() => setActiveDetailTab('routing')}
                                    className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeDetailTab === 'routing'
                                            ? 'border-blue-600 text-blue-600 bg-white'
                                            : 'border-transparent text-zinc-600 hover:text-zinc-900'
                                        }`}
                                >
                                    <Wrench size={15} /> Routing Operations & Work Centers
                                </button>
                            </div>

                            <div className="p-4">
                                {activeDetailTab === 'bom' && (
                                    <div>
                                        <div className="mb-3 text-xs text-zinc-500 flex justify-between items-center">
                                            <span><strong>BOM ID:</strong> #{selectedOrder.bom?.bom_id || 'N/A'}</span>
                                            <span><strong>Active Status:</strong> {selectedOrder.bom?.is_active ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse text-xs">
                                                <thead className="bg-zinc-50 border-y border-zinc-200 text-zinc-500 uppercase tracking-wider font-semibold">
                                                    <tr>
                                                        <th className="p-2.5">Seq</th>
                                                        <th className="p-2.5">Component ID</th>
                                                        <th className="p-2.5">Unit Qty Required</th>
                                                        <th className="p-2.5">Total Qty (Planned x Qty)</th>
                                                        <th className="p-2.5">Unit Measure</th>
                                                        <th className="p-2.5">Scrap Factor</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-zinc-100">
                                                    {selectedOrder.bom?.bom_lines && selectedOrder.bom.bom_lines.length > 0 ? (
                                                        selectedOrder.bom.bom_lines.map((line) => (
                                                            <tr key={line.bomline_id} className="hover:bg-zinc-50/80">
                                                                <td className="p-2.5 font-mono text-zinc-500">#{line.sequence}</td>
                                                                <td className="p-2.5 font-semibold text-zinc-800">{line.description}</td>
                                                                <td className="p-2.5 text-zinc-700">{line.quantity}</td>
                                                                <td className="p-2.5 font-bold text-blue-600">
                                                                    {(line.quantity * (selectedOrder.quantity_planned || 0)).toLocaleString()} {line.unit_measure}
                                                                </td>
                                                                <td className="p-2.5 text-zinc-600">{line.unit_measure}</td>
                                                                <td className="p-2.5 text-zinc-600">{line.scrap_factor}%</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="6" className="p-6 text-center text-zinc-400">
                                                                No BOM lines found for this order.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {activeDetailTab === 'routing' && (
                                    <div>
                                        <div className="mb-3 text-xs text-zinc-500 flex justify-between items-center">
                                            <span><strong>Routing ID:</strong> #{selectedOrder.routing?.routing_id || 'N/A'}</span>
                                            <span><strong>Active Status:</strong> {selectedOrder.routing?.is_active ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse text-xs">
                                                <thead className="bg-zinc-50 border-y border-zinc-200 text-zinc-500 uppercase tracking-wider font-semibold">
                                                    <tr>
                                                        <th className="p-2.5">Seq</th>
                                                        <th className="p-2.5">Operation Description</th>
                                                        <th className="p-2.5">Assigned Work Center</th>
                                                        <th className="p-2.5">Setup Time (Hrs)</th>
                                                        <th className="p-2.5">Run Time/Unit (Hrs)</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-zinc-100">
                                                    {selectedOrder.routing?.operations && selectedOrder.routing.operations.length > 0 ? (
                                                        selectedOrder.routing.operations.map((op) => (
                                                            <tr key={op.routingoperation_id} className="hover:bg-zinc-50/80">
                                                                <td className="p-2.5 font-mono text-zinc-500">#{op.sequence}</td>
                                                                <td className="p-2.5 font-semibold text-zinc-800">{op.description}</td>
                                                                <td className="p-2.5">
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-medium">
                                                                        {op.work_center?.name || `WorkCenter #${op.work_center?.workcenter_id || 'N/A'}`}
                                                                    </span>
                                                                </td>
                                                                <td className="p-2.5 text-zinc-600">{op.setup_time_hours} hrs</td>
                                                                <td className="p-2.5 text-zinc-600">{op.run_time_hours_per_unit} hrs</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="p-6 text-center text-zinc-400">
                                                                No routing operations found for this order.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="py-12 text-center text-zinc-400 text-xs border border-dashed border-zinc-200 rounded-xl">
                        No production order selected or available. Select an order below to inspect its workflow.
                    </div>
                )}
            </Card>

            {/* Production Orders Data Table & Filter Panel */}
            <Card className="p-5 border-zinc-200 shadow-sm bg-white">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-5">
                    <div className="w-full md:w-72 relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input
                            type="text"
                            placeholder="Search order ID or product..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-600 focus:outline-none"
                        />
                    </div>

                    {/* Status Filter Tabs */}
                    <div className="flex flex-wrap gap-1 border-b md:border-b-0 border-zinc-100 pb-2 md:pb-0 w-full md:w-auto">
                        {['all', 'draft', 'confirmed', 'in progress', 'completed'].map((st) => (
                            <button
                                key={st}
                                onClick={() => setStatusFilter(st)}
                                className={`px-3 py-1.5 rounded-lg text-xs capitalize transition-colors font-medium ${statusFilter === st
                                    ? 'bg-zinc-900 text-white'
                                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                                    }`}
                            >
                                {st}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Orders Table */}
                <div className="border border-zinc-200 rounded-xl overflow-x-auto text-xs">
                    <table className="w-full text-left border-collapse min-w-[600px]">
                        <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 uppercase tracking-wider font-semibold">
                            <tr>
                                <th className="p-3">Order ID</th>
                                <th className="p-3">Target Product</th>
                                <th className="p-3">Quantity</th>
                                <th className="p-3">Current Status</th>
                                <th className="p-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-zinc-400">
                                        <Loader2 size={18} className="animate-spin inline mr-2 text-blue-600" /> Fetching production orders...
                                    </td>
                                </tr>
                            ) : filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-zinc-400">
                                        No matching production orders found.
                                    </td>
                                </tr>
                            ) : (
                                filteredOrders.map((order) => {
                                    const idStr = (order.productionorder_id || order.id || '').toString();
                                    const isSelected = idStr === selectedOrderId;
                                    const currentStatus = (order.status || 'draft').toLowerCase();

                                    const statusObj = STATUS_STEPS.find(s => s.id === currentStatus) || STATUS_STEPS[0];

                                    return (
                                        <tr
                                            key={idStr}
                                            onClick={() => setSelectedOrderId(idStr)}
                                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/50 font-medium' : 'hover:bg-zinc-50/80'
                                                }`}
                                        >
                                            <td className="p-3 font-mono font-bold text-blue-600">
                                                #{idStr}
                                            </td>
                                            <td className="p-3 font-semibold text-zinc-800">
                                                {order.product_name || order.bom?.description || `Product #${order.catentry_id}` || 'N/A'}
                                            </td>
                                            <td className="p-3 text-zinc-600">
                                                {order.quantity_planned || 0} units
                                            </td>
                                            <td className="p-3">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize border ${statusObj.bg} ${statusObj.color} ${statusObj.border}`}>
                                                    <statusObj.icon size={12} />
                                                    {order.status || 'draft'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-right">
                                                <Button
                                                    className={`text-xs px-3 py-1 ${isSelected
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                                                        }`}
                                                >
                                                    {isSelected ? 'Viewing Flow' : 'View Flow'}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
};

export default ProductionOrderFlowManager;